import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions'; // Added for secure cloud execution

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      setUsers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminStatus = async (user) => {
    if (user.email === 'bikutech44@gmail.com') return alert("Protected Account: Operational changes are locked.");
    if (auth.currentUser?.uid === user.id) return alert("Cannot change your own status.");
    if (!window.confirm(`Set ${user.email} as ${user.isAdmin ? 'Standard User' : 'Admin'}?`)) return;

    setUpdatingUserId(user.id);
    try {
      await updateDoc(doc(db, 'users', user.id), { isAdmin: !user.isAdmin });
      fetchUsers();
    } catch (err) { 
      setError('Update failed.'); 
    } finally { 
      setUpdatingUserId(null); 
    }
  };

  const toggleSuspensionStatus = async (user) => {
    if (user.email === 'bikutech44@gmail.com') return alert("Protected Account: Suspension changes are locked.");
    if (auth.currentUser?.uid === user.id) return alert("Cannot alter your own suspension parameters.");
    const isSuspended = user.accountStatus === 'suspended';
    
    if (!window.confirm(`Are you sure you want to ${isSuspended ? 'UNSUSPEND' : 'SUSPEND'} user account: ${user.email}?`)) return;

    setUpdatingUserId(user.id);
    try {
      await updateDoc(doc(db, 'users', user.id), { 
        accountStatus: isSuspended ? 'active' : 'suspended' 
      });
      fetchUsers();
    } catch (err) { 
      setError('Suspension modifications failed.'); 
    } finally { 
      setUpdatingUserId(null); 
    }
  };

  // UPDATED FEATURE: Triggers total cross-system deep data purge
  const handleDeleteUser = async (user) => {
    if (user.email === 'bikutech44@gmail.com') return alert("Protected Account: Deletion requests are locked.");
    if (auth.currentUser?.uid === user.id) return alert("Cannot delete yourself.");
    
    if (!window.confirm(`⚠️ TOTAL SYSTEM DEEP WIPE WARNING:\n\nThis will trigger an official Google Cloud Function to remove this user from the live Authentication Tab, drop their Firestore profile, delete all listed rooms, and completely purge their images from Cloudinary.\n\nAre you sure you want to completely erase ${user.email}?`)) return;

    setDeletingUserId(user.id);
    try {
      // 1. Point toward your deployed Firebase Cloud Function environment container
      const functions = getFunctions();
      const deepWipeUserAccount = httpsCallable(functions, 'deepWipeUserAccount');

      console.log("Invoking cloud engine sequence execution...");
      
      // 2. Pass data payload to delete authentication, database rows, and storage files
      const cloudResult = await deepWipeUserAccount({
        targetUid: user.id,
        userEmail: user.email
      });

      if (cloudResult.data?.success) {
        // Remove locally from UI state array cleanly
        setUsers(prev => prev.filter(u => u.id !== user.id));
        alert('Total System Deep Wipe Successful! User account expunged across all platforms.');
      } else {
        throw new Error("Cloud execution engine did not return a valid success token confirmation.");
      }
    } catch (err) { 
      console.error("Deep wipe web invocation failure:", err);
      alert(`Wipe Interrupted: ${err.message}`);
    } finally { 
      setDeletingUserId(null); 
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => { fetchUsers(); }, []);

  if (loading) return <div style={styles.loading}>Connecting profile directories...</div>;

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 900px) {
          .desktop-table { display: none !important; }
          .mobile-card { display: block !important; }
        }
        @media (min-width: 901px) {
          .mobile-card { display: none !important; }
        }
      `}</style>

      <div style={styles.header}>
        <h2 style={styles.title}>User Account Directories ({filteredUsers.length})</h2>
        <input 
          style={styles.searchBar} 
          placeholder="Search by identity parameter..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {error && <p style={styles.error}>{error}</p>}

      {/* DESKTOP EVALUATION MASTER SPREADSHEET */}
      <table style={styles.table} className="desktop-table">
        <thead>
          <tr style={styles.trHead}>
            <th style={styles.th}>User Identity Metadata</th>
            <th style={styles.th}>System Role</th>
            <th style={styles.th}>Account Standing</th>
            <th style={styles.th}>Administration Actions Toolbelt</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map(user => (
            <tr key={user.id} style={styles.tr}>
              <td style={styles.td}>
                <div>
                  <strong>{user.firstName || 'Unknown'} {user.lastName || ''}</strong>
                  {user.email === 'bikutech44@gmail.com' && <span style={styles.ownerTag}>Owner</span>}
                </div>
                <div style={{fontSize: '12px', color: '#64748b'}}>{user.email}</div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginTop: '2px'}}>UID: {user.id}</div>
              </td>
              <td style={styles.td}>
                <span style={user.isAdmin ? styles.adminBadge : styles.userBadge}>
                  {user.isAdmin ? 'Admin' : 'User'}
                </span>
              </td>
              <td style={styles.td}>
                <span style={user.accountStatus === 'suspended' ? styles.suspendedBadge : styles.activeBadge}>
                  {user.accountStatus === 'suspended' ? 'SUSPENDED' : 'ACTIVE'}
                </span>
              </td>
              <td style={styles.td}>
                {/* CHECK EMAIL INJECTION SAFETY GATE */}
                {user.email?.toLowerCase() === 'bikutech44@gmail.com' ? (
                  <span style={styles.protectedText}>🔒 System Protected Account</span>
                ) : (
                  <>
                    <button onClick={() => toggleAdminStatus(user)} disabled={updatingUserId === user.id || deletingUserId !== null} style={styles.actionBtn}>
                      {updatingUserId === user.id ? '...' : (user.isAdmin ? 'Revoke Admin' : 'Grant Admin')}
                    </button>
                    
                    <button 
                      onClick={() => toggleSuspensionStatus(user)} 
                      disabled={updatingUserId === user.id || deletingUserId !== null} 
                      style={user.accountStatus === 'suspended' ? styles.unsuspendBtn : styles.suspendBtn}
                    >
                      {updatingUserId === user.id ? '...' : (user.accountStatus === 'suspended' ? 'Unsuspend' : 'Suspend')}
                    </button>

                    <button onClick={() => handleDeleteUser(user)} disabled={deletingUserId !== null || updatingUserId !== null} style={styles.delBtn}>
                      {deletingUserId === user.id ? 'Wiping...' : 'Delete'}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* MOBILE RESPONSIVE LAYOUT CONFIGURATION GRID */}
      <div className="mobile-card">
        {filteredUsers.map(user => (
          <div key={user.id} style={styles.card}>
            <div style={styles.cardFlexHeader}>
              <span style={{fontWeight: 'bold', fontSize: '15px'}}>
                {user.firstName || 'Unknown'} {user.lastName || ''}
              </span>
              <div style={{display: 'flex', gap: '4px'}}>
                <span style={user.isAdmin ? styles.adminBadge : styles.userBadge}>
                  {user.isAdmin ? 'Admin' : 'User'}
                </span>
                <span style={user.accountStatus === 'suspended' ? styles.suspendedBadge : styles.activeBadge}>
                  {user.accountStatus === 'suspended' ? 'SUSP' : 'ACTV'}
                </span>
              </div>
            </div>
            <div style={{fontSize: '13px', color: '#475569', marginBottom: '12px'}}>{user.email}</div>
            
            <div style={styles.cardActions}>
              {/* MOBILE CHECK EMAIL INJECTION SAFETY GATE */}
              {user.email?.toLowerCase() === 'bikutech44@gmail.com' ? (
                <div style={styles.mobileProtectedBanner}>🛡️ App Owner Account (Actions Locked)</div>
              ) : (
                <>
                  <button onClick={() => toggleAdminStatus(user)} disabled={updatingUserId === user.id || deletingUserId !== null} style={{...styles.actionBtn, flex: 1}}>
                    {user.isAdmin ? 'Revoke' : 'Admin'}
                  </button>
                  <button 
                    onClick={() => toggleSuspensionStatus(user)} 
                    disabled={updatingUserId === user.id || deletingUserId !== null}
                    style={{
                      flex: 1, 
                      border: 'none', 
                      borderRadius: '6px', 
                      color: 'white', 
                      fontSize: '12px', 
                      cursor: 'pointer', 
                      backgroundColor: user.accountStatus === 'suspended' ? '#10b981' : '#f59e0b'
                    }}
                  >
                    {user.accountStatus === 'suspended' ? 'Unsuspend' : 'Suspend'}
                  </button>
                  <button onClick={() => handleDeleteUser(user)} disabled={deletingUserId !== null || updatingUserId !== null} style={{...styles.delBtn, flex: 1}}>
                    {deletingUserId === user.id ? '...' : 'Delete'}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: { 
    padding: '20px', 
    backgroundColor: '#fff', 
    borderRadius: '12px', 
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' 
  },

  header: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    marginBottom: '20px', 
    flexWrap: 'wrap', 
    gap: '15px' 
  },

  title: { 
    fontSize: '18px', 
    color: '#1e293b', 
    fontWeight: '600' 
  },

  searchBar: { 
    padding: '10px 15px', 
    borderRadius: '8px', 
    border: '1px solid #e2e8f0', 
    width: '260px', 
    fontSize: '14px' 
  },

  /* Table Styles */
  table: { 
    width: '100%', 
    borderCollapse: 'collapse' 
  },

  th: { 
    textAlign: 'left', 
    padding: '12px', 
    color: '#64748b', 
    fontSize: '13px', 
    borderBottom: '2px solid #f1f5f9', 
    fontWeight: '600' 
  },

  td: { 
    padding: '14px 12px', 
    borderBottom: '1px solid #f1f5f9', 
    fontSize: '14px', 
    color: '#334155', 
    verticalAlign: 'middle' 
  },

  tr: { 
    transition: '0.2s' 
  },
  
  /* Protection Tags & Banners */
  ownerTag: { 
    backgroundColor: '#0f766e', 
    color: 'white', 
    padding: '1px 6px', 
    borderRadius: '4px', 
    fontSize: '10px', 
    marginLeft: '6px', 
    fontWeight: 'bold', 
    display: 'inline-block', 
    verticalAlign: 'middle' 
  },

  protectedText: { 
    color: '#0f766e', 
    fontWeight: '600', 
    fontSize: '13px', 
    fontStyle: 'italic', 
    paddingLeft: '4px' 
  },

  mobileProtectedBanner: { 
    width: '100%', 
    textAlign: 'center', 
    backgroundColor: '#f0fdf4', 
    color: '#0f766e', 
    padding: '8px', 
    borderRadius: '6px', 
    fontWeight: '600', 
    fontSize: '12px', 
    border: '1px solid #ccfbf1' 
  },

  /* Status Badges */
  adminBadge: { 
    backgroundColor: '#dcfce7', 
    color: '#166534', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  userBadge: { 
    backgroundColor: '#f1f5f9', 
    color: '#475569', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  activeBadge: { 
    backgroundColor: '#e0f2fe', 
    color: '#0369a1', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  suspendedBadge: { 
    backgroundColor: '#fee2e2', 
    color: '#991b1b', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },
  
  /* Buttons */
  actionBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#3b82f6', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    marginRight: '4px', 
    fontSize: '12px', 
    fontWeight: '500' 
  },

  suspendBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#f59e0b', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    marginRight: '4px', 
    fontSize: '12px', 
    fontWeight: '500' 
  },

  unsuspendBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#10b981', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    marginRight: '4px', 
    fontSize: '12px', 
    fontWeight: '500' 
  },

  delBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#ef4444', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '12px', 
    fontWeight: '500' 
  },
  
  /* Mobile Cards & States */
  card: { 
    border: '1px solid #e2e8f0', 
    borderRadius: '12px', 
    padding: '16px', 
    marginBottom: '16px', 
    backgroundColor: '#f8fafc' 
  },

  cardFlexHeader: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    marginBottom: '4px' 
  },

  cardActions: { 
    display: 'flex', 
    gap: '6px', 
    marginTop: '12px' 
  },

  loading: { 
    textAlign: 'center', 
    padding: '50px', 
    color: '#64748b' 
  },

  error: { 
    color: '#ef4444', 
    padding: '10px', 
    fontSize: '14px' 
  }

};

export default UserManagement;